import type { InboxItem } from "../../state/inbox";

export const inboxItemTitle = (item: InboxItem): string =>
  item.kind === "agent-notification"
    ? item.title
    : item.kind === "policy-review"
      ? item.regulationTitle
      : item.kind === "workflow-approval"
        ? `${item.workflowName}: ${item.stepLabel}`
        : item.organizationName;
export const notificationSourceLabel = (source: "inspector" | "insight" | "workflow") =>
  source === "inspector" ? "Inspector" : source === "insight" ? "Insight" : "Workflows";

/**
 * The tenant an item belongs to, or null for items that follow the PERSON
 * rather than a tenant (invitations — you are not a member yet — and the
 * regulatory review fixtures, which carry no organization).
 */
export const inboxItemOrganizationId = (item: InboxItem): string | null =>
  item.kind === "agent-notification" || item.kind === "workflow-approval" ? item.organizationId : null;

/** The tenant's display name, for the org tag on the cross-tenant list. */
export const inboxItemOrganizationName = (item: InboxItem): string | null =>
  item.kind === "agent-notification" || item.kind === "workflow-approval" ? item.organizationName : null;

/** Every kind but invitations carries a severity; an approval always needs action. */
export const inboxItemSeverity = (item: InboxItem): "high" | "medium" | "low" | null =>
  item.kind === "invitation" ? null : item.kind === "workflow-approval" ? "high" : item.severity;

/**
 * The org-scoped inbox is a FILTERED VIEW of the personal one, never a
 * different fetch: `/inbox` shows every tenant the person belongs to, and
 * `/$org/inbox` narrows the tenant-bound items to that org. Person-bound items
 * (invitations) are the same under every org, so they always survive — that is
 * what the org-scoped route has always shown.
 */
export function scopeInboxItems(
  items: readonly InboxItem[],
  organizationId: string | null,
): readonly InboxItem[] {
  if (!organizationId) return items;
  return items.filter((item) => {
    const owner = inboxItemOrganizationId(item);
    return owner === null || owner === organizationId;
  });
}

/** True when the list mixes tenants — the cue for showing the org tag. */
export function inboxSpansOrganizations(items: readonly InboxItem[]): boolean {
  const seen = new Set<string>();
  for (const item of items) {
    const id = inboxItemOrganizationId(item);
    if (id !== null) seen.add(id);
    if (seen.size > 1) return true;
  }
  return false;
}

export type InboxFilter = "all" | InboxItem["kind"];
export const inboxItemKey = (item: InboxItem): string => `${item.kind}:${item.id}`;

export type InboxSeverity = "all" | "high" | "medium" | "low";
export type InboxOrder = "newest" | "oldest" | "priority";

export function filterInboxItems(
  items: readonly InboxItem[],
  filter: InboxFilter,
  options: {
    query?: string;
    severity?: InboxSeverity;
    order?: InboxOrder;
    jurisdiction?: string;
  } = {},
) {
  const words = options.query?.trim().toLowerCase().split(/\s+/).filter(Boolean) ?? [];
  const filtered =
    filter === "all" &&
    !words.length &&
    !options.jurisdiction &&
    (!options.severity || options.severity === "all")
      ? items
      : items.filter((item) => {
          if (filter !== "all" && item.kind !== filter) return false;
          if (
            options.jurisdiction &&
            (item.kind !== "policy-review" || item.jurisdiction !== options.jurisdiction)
          )
            return false;
          if (
            options.severity &&
            options.severity !== "all" &&
            inboxItemSeverity(item) !== options.severity
          )
            return false;
          const searchable =
            item.kind === "agent-notification"
              ? [
                  item.title,
                  item.summary,
                  item.body,
                  item.source,
                  item.category,
                  item.organizationName,
                ]
              : item.kind === "policy-review"
                ? [item.regulationTitle, item.summary, item.jurisdiction, ...item.affectedPolicies]
                : item.kind === "workflow-approval"
                  ? [item.workflowName, item.stepLabel, item.organizationName, item.startedByEmail ?? "", item.stepKind === "checklist" ? "to do" : "approval"]
                  : [item.organizationName, item.organizationSlug, item.role];
          const text = searchable.join(" ").toLowerCase();
          return words.every((word) => text.includes(word));
        });
  if (!options.order) return filtered;
  const date = (item: InboxItem) => Date.parse(item.createdAt) || 0;
  const priority = (item: InboxItem) => {
    const severity = inboxItemSeverity(item);
    return severity === null ? 3 : ({ high: 0, medium: 1, low: 2 } as const)[severity];
  };
  return [...filtered].sort((a, b) => {
    if (options.order === "priority") {
      const difference = priority(a) - priority(b);
      if (difference !== 0) return difference;
    }
    return options.order === "oldest" ? date(a) - date(b) : date(b) - date(a);
  });
}

export function inboxFilterCount(
  filter: InboxFilter,
  severity: InboxSeverity,
  jurisdiction: string,
) {
  return Number(filter !== "all") + Number(severity !== "all") + Number(Boolean(jurisdiction));
}

export function inboxItemText(item: InboxItem): string {
  if (item.kind === "agent-notification")
    return [
      item.title,
      notificationSourceLabel(item.source),
      item.organizationName,
      "",
      ...(item.summary ? [item.summary, ""] : []),
      item.body,
    ].join("\n");
  if (item.kind === "workflow-approval")
    return [
      `${item.workflowName}: ${item.stepLabel}`,
      item.stepKind === "checklist" ? `Waiting for you to do it since ${item.waitingSince}` : `Waiting for your approval since ${item.waitingSince}`,
      ...(item.startedByEmail ? [`Started by ${item.startedByEmail}`] : []),
      item.organizationName,
    ].join("\n");
  return item.kind === "policy-review"
    ? [
        item.regulationTitle,
        item.jurisdiction,
        item.summary,
        "",
        "Affected policies",
        ...item.affectedPolicies.map((name) => `• ${name}`),
      ].join("\n")
    : [
        `Invitation to ${item.organizationName}`,
        `Role: ${item.role}`,
        `Expires: ${item.expiresAt}`,
      ].join("\n");
}

/** Keep an explicit selection across refreshes; fall back when it leaves the list. */
export function selectedInboxItem(items: readonly InboxItem[], key: string | null) {
  return items.find((item) => inboxItemKey(item) === key) ?? items[0] ?? null;
}

export const INBOX_SEVERITY = {
  high: { label: "Action needed", variant: "error", dotClass: "bg-destructive-foreground" },
  medium: { label: "Review", variant: "warning", dotClass: "bg-warning-foreground" },
  low: { label: "Informational", variant: "info", dotClass: "bg-info-foreground" },
} as const;

/** Only relate visible feed items with the same jurisdiction or invited organization. */
export function relatedInboxItems(items: readonly InboxItem[], selected: InboxItem) {
  const candidates = items.filter((item) => {
    if (inboxItemKey(item) === inboxItemKey(selected)) return false;
    // Never mix preview fixtures into the context of a real update.
    if (item.id.startsWith("sample-") !== selected.id.startsWith("sample-")) return false;
    if (selected.kind === "agent-notification")
      return (
        item.kind === "agent-notification" &&
        item.organizationId === selected.organizationId &&
        item.source === selected.source
      );
    if (selected.kind === "workflow-approval")
      return item.kind === "workflow-approval" && item.organizationId === selected.organizationId;
    return selected.kind === "policy-review"
      ? item.kind === "policy-review" && item.jurisdiction === selected.jurisdiction
      : item.kind === "invitation" && item.organizationSlug === selected.organizationSlug;
  });
  return filterInboxItems(candidates, "all", { order: "newest" }).slice(0, 3);
}

/** One line for a row: what it is, and a short second line for context. */
export function inboxItemHeadline(item: InboxItem): { title: string; detail: string } {
  switch (item.kind) {
    case "invitation":
      return { title: item.organizationName, detail: `Invitation to join as ${item.role}` };
    case "policy-review":
      return { title: item.regulationTitle, detail: item.summary };
    case "agent-notification":
      return { title: item.title, detail: item.body };
    case "workflow-approval":
      return {
        title: `${item.workflowName}: ${item.stepLabel}`,
        detail: item.startedByEmail ? `Waiting for your approval · started by ${item.startedByEmail}` : "Waiting for your approval",
      };
  }
}
